import { ref } from 'vue';
import { useToast } from './useToast';

export function useCsvImport(investmentId: string, getAuthHeader: () => any, rawMovementCategories: any, fetchMovements: () => Promise<void>) {
    const toast = useToast();
    const showImportModal = ref(false);
    const importingFile = ref(false);
    const importData = ref([]);
    const importConfig = ref({ 
        globalType: 'payment', 
        dateFormat: 'd/m/Y', 
        forceCompound: false, 
        globalLabel: '', 
        columns: [] as any[]
    });

    const parseCSVLine = (text: string, sep: string) => {
        let result = [], curVal = '', inQuotes = false;
        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            if (inQuotes) {
                if (char === '"') { 
                    if (i + 1 < text.length && text[i + 1] === '"') { curVal += '"'; i++ } 
                    else { inQuotes = false } 
                } else { curVal += char }
            } else {
                if (char === '"') { inQuotes = true } 
                else if (char === sep) { result.push(curVal.trim()); curVal = '' } 
                else { curVal += char }
            }
        }
        result.push(curVal.trim());
        return result;
    };

    const parseCSVText = (text: string) => {
        const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
        if (lines.length < 2) { toast.warning('Fichier invalide ou vide.'); return }
        
        importConfig.value.forceCompound = false;
        importConfig.value.globalLabel = '';
        const sep = lines[0].includes(';') ? ';' : ',';
        const headers = parseCSVLine(lines[0], sep);
        
        importConfig.value.columns = headers.slice(1).map(h => ({ 
            name: h, 
            ignore: false, 
            impactsCurrentValue: true, 
            impactsInvestedAmount: true 
        }));
        
        const dataRows = [];
        for (let i = 1; i < lines.length; i++) {
            const cols = parseCSVLine(lines[i], sep);
            if (cols.length >= 1) dataRows.push(cols);
        }
        
        importData.value = dataRows;
        
        // Auto-detect date format
        if (dataRows.length > 0 && dataRows[0][0]) {
            const d = dataRows[0][0];
            if (/^\d{4}-\d{2}-\d{2}$/.test(d)) importConfig.value.dateFormat = 'Y-m-d';
            else if (/^\d{2}\/\d{2}\/\d{4}$/.test(d)) importConfig.value.dateFormat = 'd/m/Y';
            else if (/^\d{2}-\d{2}-\d{4}$/.test(d)) importConfig.value.dateFormat = 'd-m-Y';
            else if (/^\d{2}\/\d{2}\/\d{2}$/.test(d)) importConfig.value.dateFormat = 'd/m/y';
            else if (/^\d{2}-\d{2}-\d{2}$/.test(d)) {
                const p = d.split('-');
                importConfig.value.dateFormat = parseInt(p[0]) > 31 ? 'y-m-d' : 'd-m-y';
            }
        }
        showImportModal.value = true;
    };

    const handleFileUpload = (event: any) => {
        const file = event.target.files[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onload = (e: any) => {
            const text = e.target.result;
            if (text.includes('\uFFFD')) {
                const fb = new FileReader();
                fb.onload = (e2: any) => parseCSVText(e2.target.result);
                fb.readAsText(file, 'windows-1252');
            } else { parseCSVText(text) }
        };
        reader.readAsText(file, 'UTF-8');
        event.target.value = '';
    };

    const parseDateString = (dateStr: string, format: string) => {
        if (!dateStr) return '';
        let y, m, d;
        if (format === 'Y-m-d') { const p = dateStr.split('-'); if (p.length !== 3) return dateStr; y = p[0]; m = p[1]; d = p[2] }
        else if (format === 'y-m-d') { const p = dateStr.split('-'); if (p.length !== 3) return dateStr; y = '20' + p[0]; m = p[1]; d = p[2] }
        else if (format === 'd/m/Y') { const p = dateStr.split('/'); if (p.length !== 3) return dateStr; y = p[2]; m = p[1]; d = p[0] }
        else if (format === 'd-m-Y') { const p = dateStr.split('-'); if (p.length !== 3) return dateStr; y = p[2]; m = p[1]; d = p[0] }
        else if (format === 'd/m/y') { const p = dateStr.split('/'); if (p.length !== 3) return dateStr; y = '20' + p[2]; m = p[1]; d = p[0] }
        else if (format === 'd-m-y') { const p = dateStr.split('-'); if (p.length !== 3) return dateStr; y = '20' + p[2]; m = p[1]; d = p[0] }
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    };

    const parseFloatString = (str: string) => {
        if (!str) return 0;
        const val = parseFloat(str.replace(/[^\d,.-]/g, '').replace(',', '.'));
        return isNaN(val) ? 0 : Math.abs(val);
    };

    const processImport = async (config: any, investmentName: string) => {
        importingFile.value = true;
        try {
            const hdrs = { 'Content-Type': 'application/json', ...getAuthHeader() };
            
            // 1. Ensure investment category exists
            let invParentCatId = null;
            let invParentCat = rawMovementCategories.value.find((c: any) => c.name === investmentName && !c.parentId);
            if (!invParentCat) {
                const pRes = await fetch('/api/movements/categories', { 
                    method: 'POST', 
                    headers: hdrs, 
                    body: JSON.stringify({ name: investmentName, parentId: null }) 
                });
                if (pRes.ok) { 
                    const j = await pRes.json(); 
                    rawMovementCategories.value.push(j); 
                    invParentCatId = j.id 
                }
            } else { invParentCatId = invParentCat.id }

            // 2. Ensure column categories exist
            const colCatMap = [];
            for (const col of config.columns) {
                if (col.ignore) { colCatMap.push(null); continue }
                let existCat = rawMovementCategories.value.find((c: any) => c.name === col.name && c.parentId === invParentCatId);
                if (!existCat) {
                    const cRes = await fetch('/api/movements/categories', { 
                        method: 'POST', 
                        headers: hdrs, 
                        body: JSON.stringify({ name: col.name, parentId: invParentCatId }) 
                    });
                    if (cRes.ok) { 
                        const j = await cRes.json(); 
                        rawMovementCategories.value.push(j); 
                        existCat = j 
                    }
                }
                colCatMap.push(existCat ? existCat.id : null);
            }

            // 3. Build movements payload
            const movementsPayload = [];
            for (const row of importData.value as any[][]) {
                const dateParsed = parseDateString(row[0], config.dateFormat);
                let amountSum = 0;
                const subMovs = [];
                for (let i = 0; i < config.columns.length; i++) {
                    const colCfg = config.columns[i];
                    if (colCfg.ignore || !colCatMap[i]) continue;
                    const val = parseFloatString(row[i + 1] || '0');
                    if (val !== 0) {
                        amountSum += val;
                        subMovs.push({ 
                            type: config.globalType, 
                            amount: val, 
                            categoryId: colCatMap[i], 
                            impactsCurrentValue: colCfg.impactsCurrentValue, 
                            impactsInvestedAmount: colCfg.impactsInvestedAmount 
                        });
                    }
                }
                if (subMovs.length === 0) continue;
                const todayStr = new Date().toISOString().split('T')[0];
                const isRel = dateParsed <= todayStr;
                const activeColumnCount = config.columns.filter((c: any, idx: number) => !c.ignore && colCatMap[idx]).length;
                const forceCompound = activeColumnCount > 1 || config.forceCompound;
                const parentLabel = (forceCompound && config.globalLabel.trim()) ? config.globalLabel.trim() : null;
                
                if (!forceCompound && subMovs.length === 1) {
                    movementsPayload.push({ 
                        type: config.globalType, 
                        amount: subMovs[0].amount, 
                        date: dateParsed, 
                        categoryId: subMovs[0].categoryId, 
                        isRealized: isRel, 
                        impactsCurrentValue: subMovs[0].impactsCurrentValue, 
                        impactsInvestedAmount: subMovs[0].impactsInvestedAmount, 
                        subMovements: [] 
                    });
                } else {
                    movementsPayload.push({ 
                        type: config.globalType, 
                        amount: amountSum, 
                        date: dateParsed, 
                        label: parentLabel, 
                        categoryId: null, 
                        isRealized: isRel, 
                        impactsCurrentValue: false, 
                        impactsInvestedAmount: false, 
                        subMovements: subMovs 
                    });
                }
            }

            if (movementsPayload.length === 0) { 
                toast.warning("Aucun mouvement valide n'a pu être extrait. Vérifiez les colonnes ignorées."); 
                return 
            }

            // 4. Send batch request
            const batchRes = await fetch('/api/movements/batch', {
                method: 'POST', 
                headers: hdrs,
                body: JSON.stringify({ 
                    investmentId, 
                    importId: 'import_' + Date.now() + '_' + Math.floor(Math.random() * 1000), 
                    movements: movementsPayload 
                })
            });
            
            if (!batchRes.ok) throw new Error("Erreur serveur lors de l'import : " + (await batchRes.text()));

            showImportModal.value = false;
            await fetchMovements();
            toast.success(`${movementsPayload.length} mouvement(s) importé(s) avec succès !`);
        } catch (e: any) {
            toast.error('Erreur lors de l\'importation : ' + e.message);
        } finally {
            importingFile.value = false;
        }
    };

    return {
        showImportModal,
        importingFile,
        importData,
        importConfig,
        handleFileUpload,
        processImport
    };
}
